import React from 'react';
import { motion } from 'framer-motion';
import { Camera, Image as ImageIcon, Sliders, Map, CheckCircle2, Star, ShieldCheck, Zap, Download, Layers, Users, Award, ChevronDown, BookOpen, Gift, Sparkles, Sun, Palette, Briefcase, Smartphone, FileText } from 'lucide-react';
import { CountdownTimer } from './components/CountdownTimer';
import { Button } from './components/Button';
import { SocialProofToast } from './components/SocialProofToast';
import { MetaPixel } from './components/MetaPixel';
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.7,
  delay
}} className={className} data-id="element-0">
    {children}
  </motion.div>;
const tomos = [{
  number: 1,
  title: 'Dominio del Modo Manual',
  description: 'Deja el modo AUTO para siempre. Aprende a controlar exposición, ISO, apertura y velocidad de obturación como un profesional.',
  icon: <Sliders className="w-7 h-7" data-id="element-1" />,
  items: ['Triángulo de exposición', 'Modos de medición', 'Balance de blancos', 'Enfoque manual vs automático', 'Histograma y exposímetro', 'Configuraciones para cada escena']
}, {
  number: 2,
  title: 'Fotografía de Aventura y Naturaleza',
  description: 'Guías de campo para las situaciones más desafiantes que encontrarás en tus viajes y expediciones fotográficas.',
  icon: <Sun className="w-7 h-7" data-id="element-2" />,
  items: ['Auroras boreales', 'Vía Láctea y astrofotografía', 'Fauna salvaje', 'Cascadas y paisajes marinos', 'Horas doradas y puestas de sol', 'Rastros de estrellas y meteoros']
}, {
  number: 3,
  title: 'Composición y Técnica Avanzada',
  description: 'Eleva tus imágenes con técnicas de composición profesional y métodos avanzados de captura.',
  icon: <Layers className="w-7 h-7" data-id="element-3" />,
  items: ['Regla de tercios y proporción áurea', 'Líneas guía y marcos naturales', 'HDR y alto rango dinámico', 'Focus stacking', 'Panoramas y timelapses', 'Exposiciones prolongadas']
}, {
  number: 4,
  title: 'Edición y Postproducción Profesional',
  description: 'Flujos de trabajo de edición paso a paso para transformar tus fotos en obras maestras.',
  icon: <Palette className="w-7 h-7" data-id="element-4" />,
  items: ['Flujo de trabajo en Lightroom', 'Retoque de retratos', 'Color grading cinematográfico', 'Blanco y negro artístico', 'Corrección de lentes y perspectiva', 'Exportación para web e impresión']
}, {
  number: 5,
  title: 'Negocio y Marca Personal',
  description: 'Convierte tu pasión en profesión. Todo lo que necesitas para lanzar y hacer crecer tu negocio fotográfico.',
  icon: <Briefcase className="w-7 h-7" data-id="element-5" />,
  items: ['Cómo fijar tus precios', 'Contratos y plantillas legales', 'Creación de portfolio profesional', 'Marketing en redes sociales', 'Gestión de clientes', 'Estrategia de marca personal']
}];
const bonos = [{
  title: '30 Presets Profesionales de Lightroom',
  description: 'Presets listos para usar que transformarán tus fotos con un solo clic. Incluye estilos cinematográficos, paisaje, retrato, blanco y negro y más.',
  value: 'ARS $14.900',
  icon: <Sparkles className="w-7 h-7" data-id="element-6" />
}, {
  title: 'Kit de Planificación de Sesiones',
  description: '25 plantillas editables para planificar cada sesión: checklists de equipo, contratos, mood boards, cronogramas y formularios para clientes.',
  value: 'ARS $11.900',
  icon: <FileText className="w-7 h-7" data-id="element-7" />
}, {
  title: 'Guía Maestra de Fotografía con Smartphone',
  description: 'Domina la cámara de tu móvil. Trucos, apps recomendadas, técnicas de composición y edición para capturar fotos increíbles sin cámara profesional.',
  value: 'ARS $9.900',
  icon: <Smartphone className="w-7 h-7" data-id="element-8" />
}];
export function App() {
  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const productImage = 'https://cdn.magicpatterns.com/uploads/mrofacSwdKLnsrHAkwwEA2/Untitled_design_(2).png';
  return <div className="min-h-screen bg-zinc-950 text-zinc-300 font-sans overflow-x-hidden" data-id="element-9">
      <MetaPixel data-id="element-10" />
      {/* Top Urgency Bar */}
      <div className="bg-amber-500 text-zinc-900 py-1.5 px-3 sticky top-0 z-50 shadow-lg" data-id="element-11">
        <div className="max-w-4xl mx-auto flex items-center justify-center gap-3 text-center" data-id="element-12">
          <p className="font-bold text-xs sm:text-sm uppercase tracking-wide flex items-center gap-1.5" data-id="element-13">
            <Zap className="w-3.5 h-3.5 fill-current" data-id="element-14" />
            ¡Oferta relámpago que termina hoy!
          </p>
          <div className="font-mono font-bold bg-zinc-950/10 px-2 py-0.5 rounded text-xs sm:text-sm flex items-center gap-1" data-id="element-15">
            <CountdownTimer initialHours={2} initialMinutes={14} initialSeconds={10} className="scale-50 sm:scale-[0.55] origin-center -my-3" data-id="element-16" />
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative pt-14 pb-16 px-4 overflow-hidden" data-id="element-17">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_var(--tw-gradient-stops))] from-zinc-800/40 via-zinc-950 to-zinc-950 -z-10" data-id="element-18" />

        <div className="max-w-5xl mx-auto text-center" data-id="element-19">
          <FadeIn data-id="element-20">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 font-semibold text-sm mb-6" data-id="element-21">
              <Star className="w-4 h-4 fill-current" data-id="element-22" />
              ¡56% de descuento sobre el precio de venta al público!
            </div>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-23">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white tracking-tight mb-6 leading-tight" data-id="element-24">
              ¿Sigues disparando <br className="hidden sm:block" data-id="element-25" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-amber-600 text-glow" data-id="element-26">
                sin un plan profesional?
              </span>
            </h1>
          </FadeIn>

          <FadeIn delay={0.15} data-id="element-27">
            <motion.div className="max-w-sm mx-auto mb-8" whileHover={{
            scale: 1.03
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 20
          }} data-id="element-28">
              <img src={productImage} alt="Fotógrafo Pro Kit — La solución fotográfica definitiva" className="w-full h-auto drop-shadow-[0_20px_60px_rgba(245,158,11,0.15)]" data-id="element-29" />
            </motion.div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-30">
            <p className="text-lg sm:text-xl text-zinc-400 max-w-3xl mx-auto mb-8 leading-relaxed" data-id="element-31">
              <strong className="text-zinc-200" data-id="element-32">Fotógrafo Pro Kit</strong>{' '}
              simplifica tu flujo de trabajo fotográfico de principio a fin. El
              kit definitivo para cualquier fotógrafo. Descárgalo, imprímelo y
              llévalo contigo siempre.
            </p>
          </FadeIn>

          <FadeIn delay={0.3} className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 mb-8 text-sm text-zinc-400 font-medium" data-id="element-33">
            <div className="flex items-center gap-2" data-id="element-34">
              <CheckCircle2 className="w-4 h-4 text-amber-500" data-id="element-35" />
              <span data-id="element-36">Miles de kits vendidos</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-37">
              <Map className="w-4 h-4 text-amber-500" data-id="element-38" />
              <span data-id="element-39">Usado en +100 países</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-40">
              <Award className="w-4 h-4 text-amber-500" data-id="element-41" />
              <span data-id="element-42">Recomendado por profesionales</span>
            </div>
          </FadeIn>

          <FadeIn delay={0.4} data-id="element-43">
            <Button size="lg" onClick={() => window.open('https://checkout.tusbeneficios.shop/cart/50929956028649:1', '_blank')} className="mx-auto" data-id="element-44">
              Obtener mi Fotógrafo Pro Kit
            </Button>
            <p className="mt-3 text-sm text-zinc-500 flex items-center justify-center gap-2" data-id="element-45">
              <ShieldCheck className="w-4 h-4" data-id="element-46" /> Garantía de devolución de 90
              días
            </p>
          </FadeIn>
        </div>
      </section>

      {/* As Seen In Placeholder */}
      <section className="border-y border-zinc-900 bg-zinc-950/50 py-6 px-4" data-id="element-47">
        <div className="max-w-5xl mx-auto" data-id="element-48">
          <p className="text-center text-xs font-bold uppercase tracking-widest text-zinc-600 mb-4" data-id="element-49">
            Como se ha publicado en
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 sm:gap-16 opacity-40 grayscale" data-id="element-50">
            <h3 className="text-xl font-serif font-bold" data-id="element-51">PhotoMag</h3>
            <h3 className="text-xl font-black tracking-tighter" data-id="element-52">
              LENS<span className="font-light" data-id="element-53">CRAFT</span>
            </h3>
            <h3 className="text-xl font-mono font-bold" data-id="element-54">APERTURE</h3>
            <h3 className="text-xl font-sans font-bold italic" data-id="element-55">ShutterPro</h3>
          </div>
        </div>
      </section>

      {/* Problem / Solution */}
      <section className="py-14 px-4 bg-zinc-950" data-id="element-56">
        <div className="max-w-3xl mx-auto space-y-6 text-base sm:text-lg text-zinc-400 leading-relaxed" data-id="element-57">
          <FadeIn data-id="element-58">
            <p data-id="element-59">
              Como fotógrafo, buscas constantemente nuevas formas de mejorar tu
              técnica y capturar imágenes impresionantes.
            </p>
          </FadeIn>
          <FadeIn data-id="element-60">
            <p data-id="element-61">
              Pero con tanta información que asimilar y recordar, puede resultar{' '}
              <strong className="text-white" data-id="element-62">abrumador</strong> hacer un
              seguimiento de todas las diferentes técnicas, configuraciones,
              flujos de edición y opciones de equipo que intervienen en la
              fotografía.
            </p>
          </FadeIn>
          <FadeIn data-id="element-63">
            <div className="p-6 rounded-2xl bg-zinc-900 border border-zinc-800 relative mt-8" data-id="element-64">
              <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-amber-500 text-zinc-950 p-2.5 rounded-full shadow-lg shadow-amber-500/20" data-id="element-65">
                <Camera className="w-5 h-5" data-id="element-66" />
              </div>
              <p className="text-zinc-300 mt-3" data-id="element-67">
                Ahí es donde entra en juego el{' '}
                <strong className="text-amber-400" data-id="element-68">Fotógrafo Pro Kit</strong>.
                Estos prácticos recursos condensan información compleja en
                formatos sencillos y fáciles de usar que te permiten consultar
                rápidamente la información que necesitas, cuando la necesitas.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-14 px-4 bg-zinc-900/30 border-y border-zinc-900" data-id="element-69">
        <div className="max-w-5xl mx-auto" data-id="element-70">
          <FadeIn data-id="element-71">
            <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-10" data-id="element-72">
              Prepárate para no volver a fallar el tiro
            </h2>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-5" data-id="element-73">
            {[{
            icon: <Download className="w-7 h-7 text-amber-500" data-id="element-74" />,
            title: 'Llévalo a todas partes',
            desc: 'Lleva contigo nuestras guías a donde quiera que vayas. Son lo suficientemente prácticas para guardarlas en tu móvil o imprimirlas para tu bolsa de cámara.'
          }, {
            icon: <Layers className="w-7 h-7 text-amber-500" data-id="element-75" />,
            title: 'Para todos los niveles',
            desc: 'Tanto si eres un profesional experimentado como si estás empezando, este kit es un recurso valioso para llevar tu fotografía al siguiente nivel.'
          }, {
            icon: <Camera className="w-7 h-7 text-amber-500" data-id="element-76" />,
            title: 'Concéntrate en disparar',
            desc: 'Al tener toda la información clave en un solo lugar, puedes concentrarte en lo verdaderamente importante: capturar fotos increíbles.'
          }].map((benefit, i) => <FadeIn key={i} delay={i * 0.1} className="bg-zinc-900/50 p-6 rounded-xl border border-zinc-800/50 hover:border-amber-500/30 transition-colors" data-id="element-77">
                <div className="bg-zinc-950 w-12 h-12 rounded-lg flex items-center justify-center mb-4 border border-zinc-800" data-id="element-78">
                  {benefit.icon}
                </div>
                <h3 className="text-lg font-bold text-white mb-2" data-id="element-79">
                  {benefit.title}
                </h3>
                <p className="text-zinc-400 text-sm leading-relaxed" data-id="element-80">
                  {benefit.desc}
                </p>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-14 px-4" data-id="element-81">
        <div className="max-w-5xl mx-auto" data-id="element-82">
          <FadeIn className="text-center mb-10" data-id="element-83">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" data-id="element-84">
              ¡Todo lo que incluye el Fotógrafo Pro Kit!
            </h2>
            <p className="text-lg text-amber-400 font-medium" data-id="element-85">
              5 Tomos completos + 3 Bonos exclusivos = Tu kit fotográfico
              definitivo
            </p>
          </FadeIn>

          {/* Section Label: Tomos */}
          <FadeIn className="mb-6" data-id="element-86">
            <div className="flex items-center gap-3 mb-1" data-id="element-87">
              <BookOpen className="w-5 h-5 text-amber-500" data-id="element-88" />
              <h3 className="text-xl font-bold text-white" data-id="element-89">Los 5 Tomos</h3>
            </div>
            <p className="text-zinc-400 text-sm" data-id="element-90">
              El corazón del Fotógrafo Pro Kit — conocimiento profesional
              organizado paso a paso.
            </p>
          </FadeIn>

          {/* Tomos Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12" data-id="element-91">
            {tomos.map((tomo, i) => <FadeIn key={i} delay={i * 0.06} className={`bg-zinc-900 border border-zinc-800 rounded-xl p-5 hover:border-amber-500/30 transition-all group ${i === 4 ? 'md:col-span-2 lg:col-span-1' : ''}`} data-id="element-92">
                <div className="flex items-center gap-3 mb-3" data-id="element-93">
                  <div className="bg-amber-500/10 text-amber-500 w-10 h-10 rounded-lg flex items-center justify-center border border-amber-500/20 group-hover:bg-amber-500/20 transition-colors" data-id="element-94">
                    {tomo.icon}
                  </div>
                  <div data-id="element-95">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-amber-500/70" data-id="element-96">
                      Tomo {tomo.number}
                    </span>
                    <h4 className="text-base font-bold text-white leading-tight" data-id="element-97">
                      {tomo.title}
                    </h4>
                  </div>
                </div>
                <p className="text-zinc-400 text-xs mb-3 leading-relaxed" data-id="element-98">
                  {tomo.description}
                </p>
                <ul className="space-y-1.5" data-id="element-99">
                  {tomo.items.map((item, j) => <li key={j} className="flex items-start gap-1.5 text-xs" data-id="element-100">
                      <CheckCircle2 className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" data-id="element-101" />
                      <span className="text-zinc-300" data-id="element-102">{item}</span>
                    </li>)}
                </ul>
              </FadeIn>)}
          </div>

          {/* Section Label: Bonos */}
          <FadeIn className="mb-6" data-id="element-103">
            <div className="flex items-center gap-3 mb-1" data-id="element-104">
              <Gift className="w-5 h-5 text-emerald-400" data-id="element-105" />
              <h3 className="text-xl font-bold text-white" data-id="element-106">
                3 Bonos Exclusivos{' '}
                <span className="text-emerald-400" data-id="element-107">GRATIS</span>
              </h3>
            </div>
            <p className="text-zinc-400 text-sm" data-id="element-108">
              Solo por tiempo limitado, recibirás estos 3 bonos valorados en más
              de ARS $36.700 totalmente gratis.
            </p>
          </FadeIn>

          {/* Bonos */}
          <div className="grid md:grid-cols-3 gap-4" data-id="element-109">
            {bonos.map((bono, i) => <FadeIn key={i} delay={i * 0.08} className="relative bg-gradient-to-b from-emerald-950/30 to-zinc-900 border border-emerald-500/20 rounded-xl p-5 hover:border-emerald-400/40 transition-all group" data-id="element-110">
                <div className="absolute -top-3 right-4 bg-emerald-500 text-zinc-950 text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full" data-id="element-111">
                  Gratis — Valor {bono.value}
                </div>
                <div className="bg-emerald-500/10 text-emerald-400 w-10 h-10 rounded-lg flex items-center justify-center border border-emerald-500/20 mb-3 group-hover:bg-emerald-500/20 transition-colors" data-id="element-112">
                  {bono.icon}
                </div>
                <h4 className="text-base font-bold text-white mb-2" data-id="element-113">
                  {bono.title}
                </h4>
                <p className="text-zinc-400 text-xs leading-relaxed" data-id="element-114">
                  {bono.description}
                </p>
              </FadeIn>)}
          </div>

          {/* Total Value Summary */}
          <FadeIn className="mt-10 text-center" data-id="element-115">
            <div className="inline-block bg-zinc-900 border border-zinc-800 rounded-xl px-6 py-4" data-id="element-116">
              <p className="text-zinc-400 text-xs uppercase tracking-wider font-bold mb-1" data-id="element-117">
                Valor total del kit completo
              </p>
              <p className="text-2xl sm:text-3xl font-black text-white" data-id="element-118">
                5 Tomos + 3 Bonos ={' '}
                <span className="text-amber-400" data-id="element-119">125+ recursos</span>
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Value Stack & Story */}
      <section className="py-14 px-4 bg-zinc-900 relative overflow-hidden" data-id="element-120">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-px bg-gradient-to-r from-transparent via-amber-500/50 to-transparent" data-id="element-121" />

        <div className="max-w-4xl mx-auto" data-id="element-122">
          <FadeIn className="prose prose-invert prose-base mx-auto text-zinc-400 mb-10" data-id="element-123">
            <p data-id="element-124">
              Nuestro equipo de fotógrafos, redactores, diseñadores gráficos y
              maquetadores ha trabajado incansablemente para ofrecerte las
              mejores guías de fotografía. Han dedicado incontables horas a
              investigar, escribir, diseñar y perfeccionar cada recurso para
              garantizar que proporcionen la información más valiosa y completa
              para fotógrafos de todos los niveles.
            </p>
            <p data-id="element-125">
              Fue un esfuerzo tremendo asegurar que el kit no solo fuera
              informativo, sino también visualmente atractivo. El arduo trabajo
              y la dedicación han dado como resultado un producto verdaderamente
              excepcional.
            </p>
          </FadeIn>

          <FadeIn className="bg-zinc-950 border-2 border-amber-500/30 rounded-2xl p-6 sm:p-8 text-center relative shadow-[0_0_50px_-12px_rgba(245,158,11,0.15)]" data-id="element-126">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-amber-500 text-zinc-950 font-bold px-5 py-1.5 rounded-full text-xs uppercase tracking-wider" data-id="element-127">
              Oferta Relámpago Actual
            </div>

            <p className="text-base text-zinc-400 mb-5 mt-3" data-id="element-128">
              Normalmente, cada recurso se vende a ARS $370, lo que supondría
              más de ARS $45.900 por todo el kit. Pero durante esta venta
              relámpago online, están disponibles por una fracción del costo.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-6" data-id="element-129">
              <div className="text-center" data-id="element-130">
                <p className="text-zinc-500 text-xs uppercase tracking-wider font-bold mb-1" data-id="element-131">
                  Precio Normal
                </p>
                <p className="text-3xl font-bold text-zinc-600 line-through decoration-red-500/50 decoration-4" data-id="element-132">
                  ARS $45.900
                </p>
              </div>
              <div className="hidden sm:block text-zinc-700" data-id="element-133">
                <ChevronDown className="w-10 h-10 -rotate-90" data-id="element-134" />
              </div>
              <div className="text-center" data-id="element-135">
                <p className="text-amber-500 text-xs uppercase tracking-wider font-bold mb-1" data-id="element-136">
                  Hoy (56% OFF)
                </p>
                <p className="text-4xl sm:text-5xl font-black text-white" data-id="element-137">
                  ARS $19.990
                </p>
              </div>
            </div>

            <div className="bg-zinc-900/50 rounded-lg p-4 flex flex-col sm:flex-row items-center gap-4 text-left border border-zinc-800" data-id="element-138">
              <ShieldCheck className="w-12 h-12 text-amber-500 shrink-0" data-id="element-139" />
              <div data-id="element-140">
                <h4 className="text-lg font-bold text-white mb-1" data-id="element-141">
                  Garantía de devolución de 90 días
                </h4>
                <p className="text-zinc-400 text-xs" data-id="element-142">
                  Si no estás completamente satisfecho con tu compra,
                  simplemente contáctanos dentro de los 90 días posteriores a la
                  compra y te reembolsaremos el importe total. Sin preguntas. Si
                  no te encanta, no pagas. Así de sencillo.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Pricing CTA */}
      <section id="pricing" className="py-14 px-4 bg-zinc-950 relative" data-id="element-143">
        <div className="max-w-3xl mx-auto text-center" data-id="element-144">
          <FadeIn data-id="element-145">
            <h2 className="text-2xl sm:text-4xl font-black text-white mb-6" data-id="element-146">
              ¡Oferta relámpago: solo quedan unas horas!
            </h2>

            <div className="mb-8" data-id="element-147">
              <CountdownTimer initialHours={2} initialMinutes={14} initialSeconds={10} data-id="element-148" />
            </div>

            <Button size="xl" fullWidth className="mb-4 shadow-2xl shadow-amber-500/20" onClick={() => window.open('https://checkout.tusbeneficios.shop/cart/50929956028649:1', '_blank')} data-id="element-149">
              Aprovecha la oferta relámpago - ARS $19.990
            </Button>

            <p className="text-zinc-500 text-sm flex items-center justify-center gap-2" data-id="element-150">
              <ShieldCheck className="w-4 h-4" data-id="element-151" /> Pago 100% seguro y encriptado
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Two Options */}
      <section className="py-14 px-4 bg-zinc-900/50 border-t border-zinc-900" data-id="element-152">
        <div className="max-w-5xl mx-auto" data-id="element-153">
          <FadeIn className="text-center mb-10" data-id="element-154">
            <h2 className="text-2xl font-bold text-white mb-3" data-id="element-155">
              ¡Es hora de tomar una decisión!
            </h2>
            <p className="text-lg text-zinc-400" data-id="element-156">
              Básicamente tienes dos opciones:
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-5" data-id="element-157">
            <FadeIn delay={0.1} className="bg-zinc-950 border border-zinc-800 p-6 rounded-2xl opacity-75" data-id="element-158">
              <div className="w-10 h-10 bg-zinc-900 rounded-full flex items-center justify-center font-bold text-zinc-500 mb-4 text-sm" data-id="element-159">
                1
              </div>
              <h3 className="text-lg font-bold text-white mb-3" data-id="element-160">
                Continúa por tu cuenta
              </h3>
              <p className="text-zinc-400 text-sm leading-relaxed" data-id="element-161">
                Recopilando consejos de fotografía de toda la internet. Puede
                que te lleve mucho tiempo, y es posible que te encuentres con
                obstáculos y cometas errores costosos. ¡Pero bueno, eso forma
                parte del aprendizaje!
              </p>
            </FadeIn>

            <FadeIn delay={0.2} className="bg-gradient-to-b from-zinc-900 to-zinc-950 border border-amber-500/30 p-6 rounded-2xl relative shadow-xl shadow-amber-500/5" data-id="element-162">
              <div className="absolute top-0 right-6 -translate-y-1/2 bg-amber-500 text-zinc-950 text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full" data-id="element-163">
                Recomendado
              </div>
              <div className="w-10 h-10 bg-amber-500/20 text-amber-500 rounded-full flex items-center justify-center font-bold mb-4 text-sm" data-id="element-164">
                2
              </div>
              <h3 className="text-lg font-bold text-white mb-3" data-id="element-165">
                Acelera tu crecimiento
              </h3>
              <p className="text-zinc-300 text-sm leading-relaxed" data-id="element-166">
                Con nuestro Fotógrafo Pro Kit. Diseñado por fotógrafos para
                fotógrafos, te ofrece una vía clara para navegar entre la
                multitud, con consejos de expertos, trucos rápidos y técnicas
                profesionales, todo en un mismo lugar. Olvídate de las
                conjeturas.
              </p>
            </FadeIn>
          </div>

          <FadeIn delay={0.3} className="mt-10 text-center" data-id="element-167">
            <Button size="lg" onClick={() => window.open('https://checkout.tusbeneficios.shop/cart/50929956028649:1', '_blank')} className="mx-auto" data-id="element-168">
              Obtén tu kit, sin riesgos
            </Button>
          </FadeIn>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-zinc-950 py-8 border-t border-zinc-900 text-center px-4" data-id="element-169">
        <div className="max-w-4xl mx-auto" data-id="element-170">
          <div className="flex flex-wrap justify-center gap-4 sm:gap-8 text-sm text-zinc-500 mb-6" data-id="element-171">
            <a href="#" className="hover:text-amber-500 transition-colors" data-id="element-172">
              Política de privacidad
            </a>
            <a href="#" className="hover:text-amber-500 transition-colors" data-id="element-173">
              Términos y condiciones
            </a>
            <a href="#" className="hover:text-amber-500 transition-colors" data-id="element-174">
              Política de reembolsos
            </a>
            <a href="#" className="hover:text-amber-500 transition-colors" data-id="element-175">
              Contacto
            </a>
          </div>
          <p className="text-zinc-600 text-sm mb-3" data-id="element-176">
            © Copyright 2026 por Fotógrafo Pro Kit, Inc.
          </p>
          <p className="text-xs text-zinc-700 max-w-2xl mx-auto" data-id="element-177">
            Este sitio no es parte del sitio web de Facebook o Facebook Inc.
            Además, este sitio NO está respaldado por Facebook de ninguna
            manera. FACEBOOK es una marca registrada de FACEBOOK, Inc.
          </p>
        </div>
      </footer>

      <SocialProofToast data-id="element-178" />
    </div>;
}